import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  Row,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'components/ui/table';
import { DataTablePagination } from './data-table-pagination';
import { DataTableViewOptions } from './data-table-view-options';
import { Input } from '../input';
import { ExportExcel } from '../ExportExcel';
import { Skeleton } from './data-table-skeleton';
import { Button } from '../ButtonGs';
import { useTranslation } from 'hooks';
import { translations } from './translations';
import { Pagination } from 'interfaces/pagination';
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  filterBy?: string;
  exportTable?: boolean;
  exportTableData?: any[];
  isLoading?: boolean;
  showControlColumns?: boolean;
  pageSize?: number;
  selectable?: boolean;
  selectionAction?: (data: any) => void;
  selectionActionLabel?: string;
  filterLabel?: string;
  exportFileName?: string;
  onRowClick?: (row: Row<TData>) => void;
  pagination?: Partial<Pagination>;
  manualPagination?: boolean;
  onPaginationChange?: (pagination: { page: number; limit: number }) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  filterBy,
  filterLabel,
  exportTable = false,
  exportTableData = [],
  isLoading = false,
  showControlColumns = true,
  pageSize = 10,
  selectable = false,
  selectionAction,
  selectionActionLabel,
  exportFileName,
  onRowClick,
  manualPagination = false,
  pagination = {
    page: 1,
    perPage: 10,
  },
  onPaginationChange,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const exportData = exportTableData.length ? exportTableData : data;
  const { getTranslation } = useTranslation(translations);

  const paginationFilter = React.useMemo(
    () => ({
      pageIndex: pagination?.page ? pagination.page - 1 : 0,
      pageSize: pagination.perPage || 10,
    }),
    [pagination?.page, pagination?.perPage]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,

    ...(manualPagination
      ? {
          manualPagination: true,
          pageCount: pagination?.lastPage,
          state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
            pagination: paginationFilter, // controlado externamente
          },
          onPaginationChange: (updater) => {
            const next =
              typeof updater === 'function'
                ? updater(table.getState().pagination)
                : updater;

            onPaginationChange?.({
              page: next.pageIndex + 1,
              limit: next.pageSize,
            });
          },
        }
      : {
          state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
          },
        }),
  });

  // Atualiza paginação sempre que mudar pageIndex ou pageSize
  React.useEffect(() => {
    if (!manualPagination || !onPaginationChange) return;

    const { pageIndex, pageSize } = table.getState().pagination;

    onPaginationChange({
      page: pageIndex + 1,
      limit: pageSize,
    });
  }, [
    manualPagination,
    onPaginationChange,
    table
  ]);

  if (isLoading) {
    return <Skeleton />;
  }

  if (!data.length) {
    return (
      <div className="flex items-center justify-center h-24 text-center font-sans">
        {getTranslation('semResultados')}
      </div>
    );
  }

  return (
    <div>
      <div className="w-full mb-4 flex flex-col md:flex-row gap-3 items-center justify-between">
        {filterBy && (
          <Input
            placeholder={`${getTranslation('filtrar')} ${
              filterLabel?.toLowerCase() ?? filterBy.toLowerCase()
            }...`}
            value={
              (table.getColumn(filterBy)?.getFilterValue() as string) ?? ''
            }
            onChange={(event) =>
              table.getColumn(filterBy)?.setFilterValue(event.target.value)
            }
            className="max-w-sm focus-visible:ring-primary-500"
          />
        )}
        {selectionAction && (
          <Button.Root
            onClick={() => selectionAction(table.getFilteredSelectedRowModel())}
            size="sm"
            disabled={!table.getFilteredSelectedRowModel().rows.length}
          >
            {selectionActionLabel ?? 'Action'} (
            {table.getFilteredSelectedRowModel().rows.length}/
            {table.getFilteredRowModel().rows.length})
          </Button.Root>
        )}
        <div className="flex flex-1 gap-3 w-full justify-end">
          {exportTable && (
            <ExportExcel data={exportData} filename={exportFileName} />
          )}
          {showControlColumns && <DataTableViewOptions table={table} />}
        </div>
      </div>
      <div className="rounded-md border bg-white">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      key={header.id}
                      className="text-sm font-GilroyBold"
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody className="text-xs font-sans">
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRowClick && onRowClick?.(row);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className="font-GilroySemibold uppercase"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center font-sans"
                >
                  {getTranslation('semResultados')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-start space-x-2 py-4">
        {selectable && (
          <div className="font-GilroyRegular flex-1 text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length}{' '}
            {getTranslation('de')} {table.getFilteredRowModel().rows.length}{' '}
            {getTranslation('linhasSelecionadas')}
          </div>
        )}
        <DataTablePagination table={table} />
      </div>
    </div>
  );
}
